import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Check, Star, ArrowRight, Zap, Target } from 'lucide-react';
import { accountPlans } from '../mockData';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import PaymentModal from './PaymentModal';

const AccountPlans = () => {
  const [selectedPlan, setSelectedPlan] = useState('instant');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const plans = selectedPlan === 'instant' ? accountPlans.instant : accountPlans.oneStep;
  const isInstant = selectedPlan === 'instant';

  const handleGetFunded = (plan) => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate('/login?redirect=/&plan=' + plan.id);
      return;
    }
    
    setSelectedPlanDetails({
      ...plan,
      type: selectedPlan === 'instant' ? 'instant' : 'onestep'
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (paymentData) => {
    // Redirect to dashboard after successful payment
    navigate('/dashboard?payment=success');
  };

  return (
    <section id="plans" style={{
      padding: '120px 24px',
      background: 'var(--bg-secondary)',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '64px'
        }}>
          <h2 style={{
            fontSize: '48px',
            fontWeight: 700,
            marginBottom: '16px',
            color: 'var(--text-primary)'
          }} className="display-md">
            {t('plans.title')}
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 40px'
          }} className="body-lg">
            {t('plans.subtitle')}
          </p>

          {/* Plan Type Selector */}
          <div style={{
            display: 'inline-flex',
            background: 'var(--bg-tertiary)',
            borderRadius: '12px',
            padding: '4px',
            border: '1px solid var(--border-primary)'
          }}>
            <button
              onClick={() => setSelectedPlan('instant')}
              style={{
                padding: '12px 32px',
                background: selectedPlan === 'instant' ? 'var(--accent-gold)' : 'transparent',
                color: selectedPlan === 'instant' ? 'var(--bg-primary)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Zap size={18} />
              {t('plans.instant')}
            </button>
            <button
              onClick={() => setSelectedPlan('oneStep')}
              style={{
                padding: '12px 32px',
                background: selectedPlan === 'oneStep' ? 'var(--accent-gold)' : 'transparent',
                color: selectedPlan === 'oneStep' ? 'var(--bg-primary)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Target size={18} />
              {t('plans.oneStep')}
            </button>
          </div>
        </div>

        {/* Plan Description Banner */}
        <div style={{
          background: isInstant ? 'rgba(212, 175, 55, 0.08)' : 'rgba(14, 165, 233, 0.08)',
          border: `1px solid ${isInstant ? 'rgba(212, 175, 55, 0.2)' : 'rgba(14, 165, 233, 0.2)'}`,
          borderRadius: '16px',
          padding: '24px 32px',
          marginBottom: '48px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '8px'
          }}>
            {isInstant ? '⚡ Instant Funding' : '🎯 One Step Challenge'}
          </h3>
          <p style={{
            fontSize: '15px',
            color: 'var(--text-secondary)',
            lineHeight: 1.6
          }}>
            {isInstant 
              ? 'Get funded immediately after purchase. Start trading live right away with stricter risk management rules and consistency requirements.'
              : 'Complete a single 10% profit target evaluation phase, then receive your funded account. More flexible rules with no consistency requirements.'
            }
          </p>
        </div>

        {/* Pricing Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '48px'
        }}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              style={{
                background: 'var(--bg-primary)',
                border: plan.popular ? '2px solid var(--accent-gold)' : '1px solid var(--border-subtle)',
                borderRadius: '20px',
                padding: '40px 32px',
                position: 'relative',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              className="hover-lift"
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--accent-gold)',
                  color: 'var(--bg-primary)',
                  padding: '6px 20px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Star size={14} fill="currentColor" />
                  Most Popular
                </div>
              )}

              {/* Capital Amount */}
              <div style={{
                fontSize: '48px',
                fontWeight: 700,
                color: 'var(--accent-gold)',
                marginBottom: '8px',
                letterSpacing: '-0.02em'
              }}>
                ${plan.capital.toLocaleString()}
              </div>

              {/* Price */}
              <div style={{
                fontSize: '16px',
                color: 'var(--text-muted)',
                marginBottom: '32px',
                paddingBottom: '32px',
                borderBottom: '1px solid var(--border-subtle)'
              }}>
                One-time fee: <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '20px' }}>${plan.price}</span>
              </div>

              {/* Features */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                marginBottom: '32px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--success)',
                    flexShrink: 0
                  }}>
                    <Check size={16} />
                  </div>
                  <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                    {plan.profitSplit} Profit Split
                  </span>
                </div>

                {isInstant ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        {plan.dailyDrawdown} Daily DD (${plan.dailyDrawdownAmount})
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        {plan.maxDrawdown} Max DD (${plan.maxDrawdownAmount})
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        {plan.maxOpenRisk} Max Open Risk
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        25% Consistency Rule
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        {plan.profitTarget} Profit Target (${plan.profitTargetAmount})
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        {plan.dailyDrawdown} Daily DD (${plan.dailyDrawdownAmount})
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        {plan.maxDrawdown} Max DD (${plan.maxDrawdownAmount})
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(16, 185, 129, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--success)',
                        flexShrink: 0
                      }}>
                        <Check size={16} />
                      </div>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                        No Consistency Rule
                      </span>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--success)',
                    flexShrink: 0
                  }}>
                    <Check size={16} />
                  </div>
                  <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                    Payout Every {plan.payoutFrequency}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--success)',
                    flexShrink: 0
                  }}>
                    <Check size={16} />
                  </div>
                  <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                    {plan.leverage} Leverage
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--success)',
                    flexShrink: 0
                  }}>
                    <Check size={16} />
                  </div>
                  <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                    {isInstant ? 'Instant Funding' : 'One Evaluation Phase'}
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <Button
                onClick={() => handleGetFunded(plan)}
                className={plan.popular ? 'btn-primary' : 'btn-secondary'}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                Get Funded Now
                <ArrowRight size={18} />
              </Button>
            </div>
          ))}
        </div>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          plan={selectedPlanDetails}
          onSuccess={handlePaymentSuccess}
        />

        {/* Info Box */}
        <div style={{
          background: 'rgba(14, 165, 233, 0.05)',
          border: '1px solid rgba(14, 165, 233, 0.2)',
          borderRadius: '16px',
          padding: '32px',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '16px',
            color: 'var(--text-secondary)',
            lineHeight: 1.6
          }}>
            <strong style={{ color: 'var(--accent-blue)' }}>All plans include:</strong> 24/7 support, real-time dashboard, {isInstant ? 'instant funding' : 'one evaluation phase'}, and access to Deriv Synthetic Indices trading with 1:1000 leverage.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AccountPlans;